import React, { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowUpRightIcon } from '@heroicons/react/24/outline'
import { parseSearchParams, getUpdatedSearchParams } from 'services/util'
import { useForm } from 'react-hook-form'
import { PagePreloader } from 'components/pagePreloader'
import { CustomTable } from 'components/customTable'
import { ColumnDef } from '@tanstack/react-table'
import { FilterOptions } from 'components/customTable/custom-table-interfaces'
import { SmoothTransition } from 'components/pageTransition'
import { FieldValues } from 'react-hook-form'
import moment from 'moment'
import { useExportVideoTrackingReport, useExportVisibleVideoTrackingRows, useVideoTrackingAppVersions, useVideoTrackingReport } from 'hooks/video-tracking'
import { SOURCE_LOCATION_LABELS, SOURCE_LOCATION_OPTIONS } from 'constants/other'

const VideoTracking: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const filterParams: any = parseSearchParams(searchParams, 'filter')

    // Initialize form with filter params
    const useFormHook = useForm<FieldValues>({
        defaultValues: {
            search: filterParams?.search || '',
            startDate: filterParams?.fromDate || filterParams?.startDate || null,
            endDate: filterParams?.toDate || filterParams?.endDate || null,
            pagination: filterParams?.pagination || { pageIndex: 0, pageSize: 10 },
            sort: filterParams?.sort || null,
            fields: filterParams?.fields || []
        }
    })

    // watch date values for input rendering
    const watchedStartDate = useFormHook.watch('startDate') as string | null
    const watchedEndDate = useFormHook.watch('endDate') as string | null
    const hasPartialDateRange = Boolean(watchedStartDate) !== Boolean(watchedEndDate)
    const canLoadData = !hasPartialDateRange
    // Date range inputs rendered in this component (keeps CustomTable unchanged)
    const dateRangeControl = (
        <div className="flex items-center gap-3 justify-end mb-2">
            <div className="flex items-center gap-2 text-xs">
                <input
                    type="date"
                    className="border px-2 py-1 rounded bg-white dark:bg-slate-700 text-sm"
                    value={watchedStartDate ? moment(watchedStartDate).format('YYYY-MM-DD') : ''}
                    onChange={(e) => useFormHook.setValue('startDate', e.target.value || null)}
                />
                {/* <label className="text-gray-500 dark:text-gray-300">From</label> */}
                <label className="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-4 z-10 origin-[0] left-2.5 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-4">App Version</label>
            </div>
            <div className="flex items-center gap-2 text-xs">
                <label className="text-gray-500 dark:text-gray-300">To</label>
                <input
                    type="date"
                    className="border px-2 py-1 rounded bg-white dark:bg-slate-700 text-sm"
                    value={watchedEndDate ? moment(watchedEndDate).format('YYYY-MM-DD') : ''}
                    onChange={(e) => useFormHook.setValue('endDate', e.target.value || null)}
                />
            </div>
        </div>
    )

    const mergedFilterParams = useMemo(() => {
        const p: any = { ...(filterParams || {}) }
        if (watchedStartDate) {
            p.fromDate = moment(watchedStartDate).format('YYYY-MM-DD')
        } else {
            delete p.fromDate
        }
        if (watchedEndDate) {
            p.toDate = moment(watchedEndDate).format('YYYY-MM-DD')
        } else {
            delete p.toDate
        }
        return p
    }, [filterParams, watchedStartDate, watchedEndDate])

    const { data, isLoading, isFetching, error, refetch } = useVideoTrackingReport(mergedFilterParams, {
        keepPreviousData: true,
        enabled: canLoadData,
    })
    const { data: appVersionsData } = useVideoTrackingAppVersions()
    const exportVideoTrackingReport = useExportVideoTrackingReport()
    const exportVisibleVideoTrackingRows = useExportVisibleVideoTrackingRows()

    const responseData: any = data?.data
    const [rowSelection, setRowSelection] = useState({})
    const [isRefreshing, setIsRefreshing] = useState(false)

    const appVersionOptions = useMemo(() => {
        const appVersions = appVersionsData?.data?.results || []
        const uniqueAppVersions: string[] = Array.from(
            new Set(
                appVersions
                    .map((item: { appVersion?: string | null }) => item?.appVersion)
                    .filter((value: string | null | undefined): value is string => Boolean(value))
            )
        )

        return uniqueAppVersions.map((appVersion) => ({
            display: appVersion,
            value: appVersion,
        }))
    }, [appVersionsData])


    const formatSourceLocation = (value: string) => {
        if (SOURCE_LOCATION_LABELS[value]) return SOURCE_LOCATION_LABELS[value]

        return value
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
    }

    const filterOptions: FilterOptions[] = useMemo(() => [
        {
            key: 'sourceLocation',
            label: 'Source Location',
            options: [
                { display: 'All', value: '' },
                ...SOURCE_LOCATION_OPTIONS.map((value) => ({
                    display: formatSourceLocation(value),
                    value,
                })),
            ],
        },
        {
            key: 'appVersion',
            label: 'App Version',
            options: [
                { display: 'All', value: '' },
                ...appVersionOptions,
            ],
        },
        // {
        //     key: 'deviceType',
        //     label: 'Platform',
        //     options: [
        //         { display: 'All', value: '' },
        //         ...DEVICE_TYPE_OPTIONS.map((value) => ({
        //             display: formatSourceLocation(value),
        //             value,
        //         })),
        //     ],
        // },
       
    ], [appVersionOptions])

    // Watch form changes and update URL params
    useEffect(() => {
        const subscription = useFormHook.watch((value, { name }) => {
            if (name) {
                const updatedParams = getUpdatedSearchParams(filterParams, value, name)
                const sp = encodeURIComponent(JSON.stringify(updatedParams))
                setSearchParams({ filter: sp })
            }
        })
        return () => subscription.unsubscribe()
    }, [filterParams, setSearchParams, useFormHook])

    // Table columns definition
    const columns: ColumnDef<any>[] = [
        // {
        //     header: 'S.No',
        //     accessorKey: 'id',
        //     cell: ({ row }) => (
        //         <div className="text-gray-900 dark:text-white">
        //             {row.index + 1}
        //         </div>
        //     ),
        // },
        {
            header: 'Contact',
            accessorKey: 'mobile',
            enableSorting: false,
            cell: ({ row }) => (
                <div className="text-gray-900 dark:text-white">
                    {row.getValue('mobile') || '-'}
                </div>
            ),
        },
        {
            header: 'Episode',
            accessorKey: 'episode_name',
            cell: ({ row }) => {
                const rating = row.getValue('episode_name') as string | number | null
                return (
                    <div className="text-gray-900 dark:text-white">
                        {rating ? (typeof rating === 'string' ? rating : rating.toFixed(1)) : '-'}
                    </div>
                )
            },
        },
        {
            header: 'Pre-loaded',
            accessorKey: 'preloaded',
            enableSorting: false,
            cell: ({ row }) => {
                const isPreloaded = row.getValue('preloaded') as any

                return (
                    <div className="text-gray-900 dark:text-white">
                        {isPreloaded}
                    </div>
                )
            },
        },
        {
            header: 'App Version',
            accessorKey: 'appVersion',
            enableSorting: false,
            cell: ({ row }) => {
                const review = row.getValue('appVersion') as string
                return (
                    <div className="max-w-xs truncate text-gray-900 dark:text-white" title={review || '-'}>
                        {review || '-'}
                    </div>
                )
            },
        },
        {
            header: 'Decoder Init (ms)',
            accessorKey: 'decoderInitMs',
            enableSorting: false,
            cell: ({ row }) => {
                const value = row.getValue('decoderInitMs') as number | null | undefined
                return (
                    <div className="text-gray-900 dark:text-white whitespace-nowrap">
                        {value ?? '-'}
                    </div>
                )
            },
        },
        {
            header: 'Decoder TS (ms)',
            accessorKey: 'decoderTsMs',
            enableSorting: false,
            cell: ({ row }) => {
                const value = row.getValue('decoderTsMs') as number | null | undefined
                return (
                    <div className="text-gray-900 dark:text-white whitespace-nowrap">
                        {value ?? '-'}
                    </div>
                )
            },
        },
        {
            header: 'Playing Started Time (ms)',
            accessorKey: 'playingStartedTimeMs',
            enableSorting: false,
            cell: ({ row }) => {
                const value = row.getValue('playingStartedTimeMs') as number | null | undefined
                return (
                    <div className="text-gray-900 dark:text-white whitespace-nowrap">
                        {value ?? '-'}
                    </div>
                )
            },
        },
        {
            header: 'Device Model',
            accessorKey: 'device_model',
            enableSorting: false,
            cell: ({ row }) => {
                const text = row.getValue('device_model') as string | null | undefined
                const display = text || '-'
                return (
                    <div className="max-w-xs truncate text-gray-900 dark:text-white" title={display}>
                        {display}
                    </div>
                )
            },
        },
        {
            header: 'Device OS',
            accessorKey: 'device_os',
            enableSorting: false,
            cell: ({ row }) => {
                const text = row.getValue('device_os') as string | null | undefined
                const display = text || '-'
                return (
                    <div className="max-w-xs truncate text-gray-900 dark:text-white" title={display}>
                        {display}
                    </div>
                )
            },
        },
        {
            header: 'Resolution',
            accessorKey: 'resolution',
            enableSorting: false,
            cell: ({ row }) => {
                const text = row.getValue('resolution') as string | null | undefined
                const display = text || '-'
                return (
                    <div className="max-w-xs truncate text-gray-900 dark:text-white" title={display}>
                        {display}
                    </div>
                )
            },
        },
        {
            header: 'Network',
            accessorKey: 'network',
            enableSorting: false,
            cell: ({ row }) => {
                const text = row.getValue('network') as string | null | undefined
                const display = text || '-'
                return (
                    <div className="max-w-xs truncate text-gray-900 dark:text-white" title={display}>
                        {display}
                    </div>
                )
            },
        },
        {
            header: 'RAM',
            accessorKey: 'ram',
            enableSorting: false,
            cell: ({ row }) => {
                const text = row.getValue('ram') as string | null | undefined
                const display = text || '-'
                return (
                    <div className="max-w-xs truncate text-gray-900 dark:text-white" title={display}>
                        {display}
                    </div>
                )
            },
        },
        {
            header: 'Bandwidth',
            accessorKey: 'bandwidth',
            enableSorting: false,
            cell: ({ row }) => {
                const text = row.getValue('bandwidth') as string | null | undefined
                const display = text || '-'
                return (
                    <div className="max-w-xs truncate text-gray-900 dark:text-white" title={display}>
                        {display}
                    </div>
                )
            },
        },
                {
            header: 'Player Bandwidth',
            accessorKey: 'bandwidthPlayer',
            enableSorting: false,
            cell: ({ row }) => {
                const text = row.getValue('bandwidthPlayer') as string | null | undefined
                const display = text || '-'
                return (
                    <div className="max-w-xs truncate text-gray-900 dark:text-white" title={display}>
                        {display}
                    </div>
                )
            },
        },
        {
            header: 'Source Location',
            accessorKey: 'sourceLocation',
            cell: ({ row }) => {
                const rating = row.getValue('sourceLocation') as string | number | null
                return (
                    <div className="text-gray-900 dark:text-white">
                        {rating ? (typeof rating === 'string' ? rating : rating.toFixed(1)) : '-'}
                    </div>
                )
            },
        },
        {
            header: 'Thumbnail Click Time',
            accessorKey: 'thumbnailClickTime_readable',
            enableSorting: false,
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null

                return (
                    <div className="text-gray-900 dark:text-white whitespace-nowrap">
                        {value === null
                            ? '-'
                            : moment(value).format('DD MMM YYYY, hh:mm:ss.SSS A')}
                    </div>
                )
            },
        },
        {
            header: 'First Frame Time',
            accessorKey: 'firstFrameTime_readable',
            enableSorting: false,
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null

                return (
                    <div className="text-gray-900 dark:text-white whitespace-nowrap">
                        {value === null
                            ? '-'
                            : moment(value).format('DD MMM YYYY, hh:mm:ss.SSS A')}
                    </div>
                )
            },
        },
        {
            header: 'Total Load Time',
            accessorKey: 'totalLoadTime_ms',
            enableSorting: false,
            cell: ({ row }) => {
                const review = row.getValue('totalLoadTime_ms') as string
                return (
                    <div className="max-w-xs truncate text-gray-900 dark:text-white" title={review || '-'}>
                        {review || '-'}
                    </div>
                )
            },
        },
        {
            header: 'Preload Duration (ms)',
            accessorKey: 'preloadDuration',
            enableSorting: false,
            cell: ({ row }) => {
                const review = row.getValue('preloadDuration') as string
                return (
                    <div className="max-w-xs truncate text-gray-900 dark:text-white" title={review || '-'}>
                        {review || '-'}
                    </div>
                )
            }
        },
{
    header: 'Preload Resolution',
    accessorKey: 'preloadResolutionMaxWidth',
    enableSorting: false,
    cell: ({ row }) => {
        const review = row.getValue('preloadResolutionMaxWidth') as string
        return (
            <div className="max-w-xs truncate text-gray-900 dark:text-white" title={review || '-'}>
                {review || '-'}
            </div>
        )
    }
},
  {
    header: 'Preload Max Height',
    accessorKey: 'preloadResolutionmaxHeight',
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.getValue('preloadResolutionmaxHeight') as string
      return (
        <div className="max-w-xs truncate text-gray-900 dark:text-white" title={value || '-'}>
          {value || '-'}
        </div>
      )
    },
  },
  {
    header: 'Player Max Width',
    accessorKey: 'playerResolutionMaxWidth',
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.getValue('playerResolutionMaxWidth') as string
      return (
        <div className="max-w-xs truncate text-gray-900 dark:text-white" title={value || '-'}>
          {value || '-'}
        </div>
      )
    },
  },
  {
    header: 'Player Max Height',
    accessorKey: 'playerResolutionmaxHeight',
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.getValue('playerResolutionmaxHeight') as string
      return (
        <div className="max-w-xs truncate text-gray-900 dark:text-white" title={value || '-'}>
          {value || '-'}
        </div>
      )
    },
  },
  {
    header: 'Min Buffer (ms)',
    accessorKey: 'minBufferMs',
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.getValue('minBufferMs') as number
      return (
        <div className="text-gray-900 dark:text-white">
          {value ?? '-'}
        </div>
      )
    },
  },
  {
    header: 'Max Buffer (ms)',
    accessorKey: 'maxBufferMs',
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.getValue('maxBufferMs') as number
      return (
        <div className="text-gray-900 dark:text-white">
          {value ?? '-'}
        </div>
      )
    },
  },
  {
    header: 'Buffer for Playback (ms)',
    accessorKey: 'bufferForPlaybackMs',
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.getValue('bufferForPlaybackMs') as number
      return (
        <div className="text-gray-900 dark:text-white">
          {value ?? '-'}
        </div>
      )
    },
  },
  {
    header: 'Buffer After Rebuffer (ms)',
    accessorKey: 'bufferAfterRebufferMs',
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.getValue('bufferAfterRebufferMs') as number
      return (
        <div className="text-gray-900 dark:text-white">
          {value ?? '-'}
        </div>
      )
    },
  },
  {
    header: 'Total Bytes Loaded',
    accessorKey: 'totalBytesLoaded',
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.getValue('totalBytesLoaded') as number
      return (
        <div className="text-gray-900 dark:text-white">
          {value ?? '-'}
        </div>
      )
    },
  },
  {
    header: 'Total Load Time (ms)',
    accessorKey: 'totalLoadTimeMs',
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.getValue('totalLoadTimeMs') as number
      return (
        <div className="text-gray-900 dark:text-white">
          {value ?? '-'}
        </div>
      )
    },
  },
        {
            header: 'Date',
            accessorKey: 'date_time',
            enableSorting: false,
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null
                return (
                    <div className="text-gray-900 dark:text-white">
                        {value ? moment(value).format('DD MMM YYYY, hh:mm:ss A') : '-'}
                    </div>
                )
            },
        },
    ]

    const handleRefresh = () => {
        if (!canLoadData) return

        setIsRefreshing(true)
        refetch().finally(() => {
            setIsRefreshing(false)
        })
    }

    const handleExportCurrentPage = () => {
        exportVisibleVideoTrackingRows(responseData?.results || [])
    }

    const handleExportFullFiltered = () => {
        if (!canLoadData) return

        const { pagination, ...filtersWithoutPagination } = filterParams || {}
        exportVideoTrackingReport(filtersWithoutPagination)
    }

    const additionalOptions = [
        // {
        //     Icon: ArrowUpRightIcon,
        //     text: 'Export Report',
        //     onClick: handleExportCurrentPage
        // },
        {
            Icon: ArrowUpRightIcon,
            text: 'Export Report',
            onClick: handleExportFullFiltered
        },
    ]

    if (isLoading) {
        return <PagePreloader />
    }

    if (error) {
        return (
            <SmoothTransition>
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg text-red-600">
                        Error: {error instanceof Error ? error.message : 'An error occurred'}
                    </div>
                </div>
            </SmoothTransition>
        )
    }

    return (
        <SmoothTransition>
            <div className='w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md'>
                <CustomTable
                    columns={columns}
                    data={responseData?.results || []}
                    pages={responseData?.pages || 0}
                    rowSelectionState={[rowSelection, setRowSelection]}
                    isLoading={isFetching || isRefreshing}
                    isErrored={!!error}
                    formHook={useFormHook}
                    title="Video Tracking Logger"
                    onRefresh={handleRefresh}
                    totalRecords={responseData?.total}
                    titleLabel={`Total ${responseData?.total || 0} records`}
                    filterOptions={filterOptions}
                    searchVisible={true}
                    extraFilterElement={dateRangeControl}
                    additionalOptions={additionalOptions}
                    additionalMessage={hasPartialDateRange ? (
                        <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                            Please select both From and To dates, or clear both dates to load all video tracking data.
                        </div>
                    ) : undefined}
                />
            </div>
        </SmoothTransition>
    )
}

export default VideoTracking
